import React, { useState, useEffect, useLayoutEffect, useMemo } from "react";
import { useNavigate } from 'react-router-dom';
import {
  FilePlus,
  FileText,
  Link,
  Trash2,
  CloudUpload,
  Upload,
  X,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  AlertTriangle,
  ArrowRight,
} from "lucide-react";
import toast from "react-hot-toast";
import ReactMarkdown from "react-markdown";
import ApiService from "../../services/ApiService";
import useBillingStore from "../../store/useBillingStore";

function KnowledgeVault() {
  const navigate = useNavigate();
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(false);
  const { billingInfo, fetchBillingInfo } = useBillingStore();
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  // Import website flow state
  const [showImportModal, setShowImportModal] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [sitemapUrls, setSitemapUrls] = useState([]); // flattened list
  const [selectedUrl, setSelectedUrl] = useState("");
  const [queuing, setQueuing] = useState(false);
  const [groupedUrls, setGroupedUrls] = useState({}); // { groupName: [urls] }
  const [groupExpanded, setGroupExpanded] = useState({}); // { groupName: bool }
  const [selectedUrls, setSelectedUrls] = useState(new Set());
  const [hasDiscovered, setHasDiscovered] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    title: "",
    contentType: "text",
    content: "",
    file: null,
  });

  const [selectedVault, setSelectedVault] = useState(null); // null = grid, object = details
  const [contentOpen, setContentOpen] = useState(false);
  const [faqsOpen, setFaqsOpen] = useState(false);
  const [expandedFaqs, setExpandedFaqs] = useState({});


  const [jobStatus, setJobStatus] = useState(null)
  const [jobId, setJobId] = useState(null);
  const [jobVisible, setJobVisible] = useState(false);
  const [polling, setPolling] = useState(false);

  const usageLimitReached = billingInfo &&
  billingInfo.limits &&
  billingInfo.usage &&
  (billingInfo.usage.uploads_count >= billingInfo.limits.uploads_count ||
    billingInfo.usage.total_file_size >= billingInfo.limits.total_file_size ||
    billingInfo.usage.tokens >= billingInfo.limits.tokens);

  const getUsageLimitMessage = () => {
    if (!billingInfo || !billingInfo.limits || !billingInfo.usage) return '';

    if (billingInfo.usage.tokens >= billingInfo.limits.tokens) {
      return 'You have exceeded your token usage limit.';
    }
    if (billingInfo.usage.uploads_count >= billingInfo.limits.uploads_count) {
      return 'You have exceeded your file upload count limit.';
    }
    if (billingInfo.usage.total_file_size >= billingInfo.limits.total_file_size) {
      return 'You have exceeded your total file size limit.';
    }
    return 'Usage limit exceeded.';
  };

  // Helper to compute exceeded quotas (same logic used in other tabs)
  const getExceededDetails = () => {
    // Only show tokens, uploads_count, total_file_size for Knowledge Vault
    if (!billingInfo || !billingInfo.limits || !billingInfo.usage) return [];
    const details = [];
    const tokensUsed = billingInfo.usage?.tokens;
    const tokensLimit = billingInfo.limits?.tokens;
    if (typeof tokensUsed === 'number' && typeof tokensLimit === 'number' && tokensUsed >= tokensLimit) {
      details.push({ key: 'tokens', title: 'AI Tokens', used: tokensUsed, limit: tokensLimit, why: 'AI token quota exhausted — AI processing and optimizations are paused. Upgrade to continue AI processing.' });
    }
    const uploadsUsed = billingInfo.usage?.uploads_count;
    const uploadsLimit = billingInfo.limits?.uploads_count;
    if (typeof uploadsUsed === 'number' && typeof uploadsLimit === 'number' && uploadsUsed >= uploadsLimit) {
      details.push({ key: 'uploads_count', title: 'File Uploads', used: uploadsUsed, limit: uploadsLimit, why: 'You have reached your file upload count limit — you cannot upload more files. Upgrade to increase upload allowance.' });
    }
    const bytesUsed = billingInfo.usage?.total_file_size;
    const bytesLimit = billingInfo.limits?.total_file_size;
    if (typeof bytesUsed === 'number' && typeof bytesLimit === 'number' && bytesUsed >= bytesLimit) {
      details.push({ key: 'total_file_size', title: 'Upload Bytes', used: bytesUsed, limit: bytesLimit, why: 'Your total uploaded data size limit has been reached — please upgrade to add more storage and continue uploading.' });
    }
    return details;
  };



  const toggleFaq = (i) => {
    setExpandedFaqs((prev) => ({ ...prev, [i]: !prev[i] }));
  };

  const getGroupName = (urlStr) => {
    try {
      const u = new URL(urlStr);
      const parts = u.pathname.split('/').filter(Boolean);
      if (parts.length === 0) return 'Root';
      // Capitalize first segment
      const first = parts[0];
      return first.charAt(0).toUpperCase() + first.slice(1);
    } catch {
      // Fallback for relative URLs
      const path = urlStr.startsWith('http') ? '/' : urlStr;
      const parts = path.split('/').filter(Boolean);
      if (parts.length === 0) return 'Root';
      const first = parts[0];
      return first.charAt(0).toUpperCase() + first.slice(1);
    }
  };

  const toDisplayPath = (urlStr) => {
    try {
      const u = new URL(urlStr);
      return u.pathname || '/';
    } catch {
      return urlStr;
    }
  };

  // Normalize a URL or path to a comparable pathname (no trailing slash)
  const normalizePath = (urlStr) => {
    try {
      const u = new URL(urlStr);
      const p = u.pathname || '/';
      return p.replace(/\/+$/,'') || '/';
    } catch {
      const p = (urlStr || '/');
      return p.replace(/\/+$/,'') || '/';
    }
  };

  // Build a set of paths that already have content uploaded (processed)
  const processedPathSet = useMemo(() => {
    const set = new Set();
    (content || []).forEach((item) => {
      if (item && item.original_url) {
        set.add(normalizePath(item.original_url));
      }
    });
    return set;
  }, [content]);

  const isUrlProcessed = (urlStr) => processedPathSet.has(normalizePath(urlStr));

  // ============== Import Website (Sitemap) helpers ==============
  const flattenSitemap = (node) => {
    // node is an object mapping sitemap-url -> array | nested object
    const out = new Set();
    const walk = (obj) => {
      if (!obj || typeof obj !== 'object') return;
      for (const key of Object.keys(obj)) {
        const val = obj[key];
        if (Array.isArray(val)) {
          for (const url of val) out.add(url);
        } else if (val && typeof val === 'object') {
          walk(val);
        }
      }
    };
    walk(node);
    return Array.from(out);
  };
  const refreshSitemapDiscovery = async () => {
    try {
      setDiscovering(true);
      const data = await ApiService.fetchSitemapURLs();
      if (data && data.success) {
        const flattened = flattenSitemap(data.urls || {});
        setSitemapUrls(flattened);
        // group them
        const groups = {};
        flattened.forEach((u) => {
          const g = getGroupName(u);
          if (!groups[g]) groups[g] = [];
          groups[g].push(u);
        });
        // sort groups and URLs
        const sortedGroups = Object.fromEntries(
          Object.keys(groups)
            .sort()
            .map((k) => [k, groups[k].sort((a, b) => toDisplayPath(a).localeCompare(toDisplayPath(b)))])
        );
        setGroupedUrls(sortedGroups);
        // if first discovery, default expand first few groups
        if (!hasDiscovered) {
          const exp = {};
          Object.keys(sortedGroups).forEach((k, idx) => { exp[k] = idx < 3; });
          setGroupExpanded(exp);
        }
        setHasDiscovered(true);
      } else {
        toast.error("Failed to discover sitemap URLs");
        setSitemapUrls([]);
        setGroupedUrls({});
      }
    } catch (e) {
      toast.error("Error discovering sitemap URLs");
      setSitemapUrls([]);
      setGroupedUrls({});
    } finally {
      setDiscovering(false);
    }
  };

  const openImportModal = async () => {
    setSelectedVault(null); // Clear selected vault to show grid view
    setShowImportModal(true);
    // Only fetch the first time; subsequent opens use cached groups and selections
    if (!hasDiscovered) {
      await refreshSitemapDiscovery();
    }
  };

  const handleQueueSelectedUrl = async () => {
    // Prevent queuing if usage limits are reached
    if (usageLimitReached) {
      toast.error(getUsageLimitMessage());
      return;
    }
    const toQueue = selectedUrls.size > 0 ? Array.from(selectedUrls) : (selectedUrl ? [selectedUrl] : []);
    if (toQueue.length === 0) { toast.error("Please select at least one URL"); return; }
    try {
      setQueuing(true);
      // Try Cloud Tasks first
      let res;
      try {
        res = await ApiService.createCloudTasks(toQueue);
      } catch (e) {
        res = null;
      }

      if (res && res.job_id) {
        toast.success(`Queued ${toQueue.length} URL(s). Job ID: ${res.job_id}`);

        // enable the loader/progress tracker after 3-4 seconds
        setJobId(res.job_id);
        setTimeout(() => { setPolling(true); }, Math.random() * 1000);
        setShowImportModal(false);
      } 
    } catch (e) {
      toast.error("Error while queuing processing");
    } finally {
      setQueuing(false);
    }
  };

  const toggleGroupExpand = (group) => {
    setGroupExpanded((prev) => ({ ...prev, [group]: !prev[group] }));
  };

  const isChecked = (url) => selectedUrls.has(url);
  const toggleUrl = (url) => {
    if (isUrlProcessed(url)) return; // prevent selecting already processed URLs
    setSelectedUrls((prev) => {
      const next = new Set(prev);
      if (next.has(url)) next.delete(url); else next.add(url);
      return next;
    });
  };

  const groupAllChecked = (group) => {
    const urls = (groupedUrls[group] || []).filter((u) => !isUrlProcessed(u));
    if (urls.length === 0) return false;
    for (const u of urls) if (!selectedUrls.has(u)) return false;
    return true;
  };

  const toggleGroup = (group) => {
    setSelectedUrls((prev) => {
      const next = new Set(prev);
      const urls = (groupedUrls[group] || []).filter((u) => !isUrlProcessed(u));
      const allChecked = urls.length > 0 && urls.every((u) => next.has(u));
      if (allChecked) {
        urls.forEach((u) => next.delete(u));
      } else {
        urls.forEach((u) => next.add(u));
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    const all = new Set();
    Object.values(groupedUrls).forEach((arr) => arr.forEach((u) => { if (!isUrlProcessed(u)) all.add(u); }));
    setSelectedUrls(all);
  };

  const handleClearAll = () => setSelectedUrls(new Set());

  useEffect(() => {
  loadContent();
  fetchBillingInfo();
  }, [fetchBillingInfo]);

  useLayoutEffect(() => {
    if (selectedVault) {
      window.scrollTo(0, 0);
    }
  }, [selectedVault]);

  //fetch the kv data
  const loadContent = async () => {
    setLoading(true);
    try {
      const data = await ApiService.getContentList();
      setContent(data.content || []);

      if (data?.ongoing_job !== null) {
        setJobId(data?.ongoing_job);
        setTimeout(() => { setPolling(true); }, Math.random() * 1000);
      }
    } catch (error) {
      toast.error("Failed to load the content");
    } finally {
      setLoading(false);
    }
  };

  



  // Function to handle FAQ navigation
  const handleFAQNavigation = (question) => {
    // Navigate to Answer Optimization with the question as a search parameter
    navigate(`/optimization?search=${encodeURIComponent(question)}`);
  };


  //data upload
  const handleUpload = async (e) => {
    e.preventDefault();

    setUploading(true);

    // Prevent upload if usage limits are reached
    if (usageLimitReached) {
      setUploading(false);
      toast.error(getUsageLimitMessage());
      return;
    }

    // title is now optional - will be auto-generated if empty

    if (uploadForm.contentType === "text" && !uploadForm.content) {
      toast.error("Please enter content");
      return;
    }

    if (uploadForm.contentType === "pdf" && !uploadForm.file) {
      toast.error("Please select a PDF file");
      return;
    }

    if (uploadForm.contentType === "docx" && !uploadForm.file) {
      toast.error("Please select a DOCX file");
      return;
    }

    if (uploadForm.contentType === "csv" && !uploadForm.file) {
      toast.error("Please select a CSV file");
      return;
    }

    if (uploadForm.contentType === "url" && !uploadForm.content) {
      toast.error("Please enter a URL");
      return;
    }

    try {
      const formData = new FormData();

      formData.append("title", uploadForm.title);
      formData.append("content_type", uploadForm.contentType);

      // Handle file uploads (PDF, DOCX, and CSV)
      if (
        (uploadForm.contentType === "pdf" ||
          uploadForm.contentType === "docx" ||
          uploadForm.contentType === "csv") &&
        uploadForm.file
      ) {
        formData.append("file", uploadForm.file);
      }

      // Handle text content and URLs
      else if (
        uploadForm.contentType === "text" ||
        uploadForm.contentType === "url"
      ) {
        formData.append("content", uploadForm.content);
      }

      const result = await ApiService.uploadContent(formData);

      if (result.success) {
        toast.success(
          `Your content is being proccessed, please refresh after some time`
        );
        setShowUploadModal(false);
        setUploadForm({
          title: "",
          contentType: "text",
          content: "",
          file: null,
        });
        loadContent();
      } else {
        toast.error("Failed to upload content");
      }
    } catch (error) {
      toast.error("Something went wrong while proccessing your request");
    } finally {
      setUploading(false);
    }
  };

  //kv delete
  const handleDelete = async (contentId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this content? This will also remove all associated FAQs."
      )
    ) {
      return;
    }

    try {
      const result = await ApiService.deleteContent(contentId);

      if (result.success) {
        toast.success("Content deleted successfully");
        loadContent();
      } else {
        toast.error("Failed to delete content");
      }
    } catch (error) {
      toast.error("Failed to delete content");
    }
  };

  const getContentIcon = (type) => {
    switch (type) {
      case "pdf":
        return FileText;

      case "csv":
        return FileText;

      case "docx":
        return FileText;

      case "url":
        return Link;

      default:
        return FileText;
    }
  };

  /**
   * Progress bar for the website import module
   */
  // const [jobStatus, setJobStatus] = useState(null);
  // polling logic (send the req every 5 seconds)
  useEffect(() => {
    let intervalId;
  
    if (polling && jobId) {
      intervalId = setInterval(async () => {
        try {
          // const result = await ApiService.deleteContent(contentId);
          const result = await ApiService.trackJobProgress(jobId);
          setJobStatus(result);

          if ( result?.job_status === "job_completed" || result?.job_status === "job_creation_failed") {
            setPolling(false);
          }

        } catch (err) {
          console.error("Error fetching job status:", err);
        }
      }, 6000); // poll every 6 sec
    }
  
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [polling, jobId]);
  // }, [polling, orgId, jobId]);

  //render the progress bar
  const renderProgressBar = () => {
    if (!jobStatus) return null;
  
    const message = jobStatus?.job_status_message || "Processing job...";
    const success = jobStatus?.success || 0;
    const pending = jobStatus?.pending || 0;
    const failed = jobStatus?.failed || 0;
    const total = success + failed + pending || 1; // Prevent division by zero
  
    const successPercent = ((success / total) * 100).toFixed(0);
    const failedPercent = ((failed / total) * 100).toFixed(0);
    const pendingPercent = 100 - successPercent - failedPercent;
  
    return (
      <div className="space-y-4">
        {/* Dynamic Progress Bar */}
        <p className="text-gray-600  font-medium mt-2">{message}</p>
        <div className="relative w-full h-8 bg-gray-200 rounded-full overflow-hidden flex shadow-inner">
          {/* Success Bar */}
          <div style={{ width: `${successPercent}%` }} className="bg-emerald-500 flex items-center justify-center transition-all duration-300">
            {successPercent > 10 && ( // Only show label if there's enough space
              <span className="text-xs font-bold text-white drop-shadow-md">
                {successPercent}%
              </span>
            )}
          </div>
          {/* Failed Bar */}
          <div style={{ width: `${failedPercent}%` }} className="bg-red-500 flex items-center justify-center transition-all duration-300">
            {failedPercent > 10 && ( // Only show label if there's enough space
              <span className="text-xs font-bold text-white drop-shadow-md">
                {failedPercent}%
              </span>
            )}
          </div>
          {/* Pending Bar */}
          <div style={{ width: `${pendingPercent}%` }} className="bg-blue-400 flex items-center justify-center transition-all duration-300">
            {pendingPercent > 10 && ( // Only show label if there's enough space
              <span className="text-xs font-bold text-white drop-shadow-md">
                {pendingPercent}%
              </span>
            )}
          </div>
        </div>
        
        {/* Detailed Status Breakdown (for larger screens) */}
        <div className="flex flex-col sm:flex-row justify-between text-sm text-gray-700 font-medium">
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-emerald-500"></span>
            <span>Success: {success}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-red-500"></span>
            <span>Failed: {failed}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-blue-400"></span>
            <span>Pending: {pending}</span>
          </div>
          <div className="flex items-center gap-1.5 font-bold text-gray-800 mt-2">
            <span>Total: {total}</span>
          </div>
        </div>
      </div>
    );
  };


  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 md:gap-4">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
            Knowledge Vault
          </h2>
          <p className="text-gray-600 mt-1 md:mt-2 max-w-2xl">
            Upload domain-specific content to automatically generate FAQs and
            enhance your AI knowledge base
          </p>
        </div>
        <div className="flex w-full md:w-auto flex-col md:flex-row gap-2 md:gap-3 md:justify-end items-stretch md:items-center">
          <button
            onClick={() => {
              // Always allow opening the upload modal; prevent actual upload at submit time if limits exceeded
              setSelectedVault(null);
              setShowUploadModal(true);
            }}
            className="inline-flex h-11 items-center justify-center w-full md:w-auto md:min-w-[180px] px-5 text-sm bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-medium rounded-xl shadow-lg hover:shadow-xl hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FilePlus className="h-5 w-5 mr-2" />
            Upload Content
          </button>
          <button
            onClick={() => {
              // Allow opening the import modal even if limits are exceeded; enforce on queue
              openImportModal();
            }}
            className="inline-flex h-11 items-center justify-center w-full md:w-auto md:min-w-[180px] px-5 text-sm bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-medium rounded-xl shadow-lg hover:shadow-xl hover:from-emerald-600 hover:to-teal-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Link className="h-5 w-5 mr-2" />
            Import Website
          </button>
        </div>
      </div>

      {/* Permanent Quota Banner */}
      {(() => {
        const exceeded = getExceededDetails();
        if (!exceeded || exceeded.length === 0) return null;
        return (
          <div className="mt-4 rounded-xl border border-red-200 bg-red-50/60 backdrop-blur p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <div className="h-9 w-9 rounded-lg bg-red-100 flex items-center justify-center border border-red-200">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div>
                    <h4 className="text-base sm:text-lg font-semibold text-red-900">
                      Usage limit reached
                    </h4>
                    <p className="text-sm text-red-700/90 mt-0.5">
                      You've reached the following monthly quota{exceeded.length>1?'s':''}. Upgrade to increase limits and continue uploading/importing content.
                    </p>
                  </div>
                  <a
                    href="/billing"
                    className="inline-flex items-center gap-2 rounded-lg bg-red-600 text-white px-3 py-2 text-sm font-semibold shadow-sm hover:bg-red-700 transition-colors"
                  >
                    View billing & upgrade
                    <ArrowRight className="h-4 w-4" />
                  </a>
                </div>

                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {exceeded.map((d) => {
                    const percent = d.limit > 0 ? Math.min(100, Math.round((d.used / d.limit) * 100)) : 100;
                    return (
                      <div key={d.key} className="rounded-lg border border-red-200 bg-white/60 p-3">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium uppercase tracking-wide text-red-700/80">
                            {d.title}
                          </span>
                          <span className="text-[11px] font-semibold text-red-700 bg-red-100 border border-red-200 rounded-full px-2 py-0.5">
                            {d.used} / {d.limit}
                          </span>
                        </div>
                        <div className="mt-2 h-2 rounded-full bg-red-100 border border-red-200 overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-red-500 to-red-600"
                            style={{ width: `${percent}%` }}
                          />
                        </div>
                        <p className="mt-2 text-xs text-red-700/90 leading-relaxed">
                          {d.why}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        );
      })()}


      
      {/* Job progress card goes here */}
        {jobStatus && jobVisible && (
          <div className="bg-white shadow-lg rounded-xl p-4 space-y-4 border">
          {/* Header with Controls */}
          <div className="flex justify-between items-center">
            <h3 className="font-semibold text-lg">Job Status</h3>
            <div className="flex gap-2">
              {/* Refresh Button */}
              <button 
                onClick={() => setPolling(true)} 
                className="px-3 py-1 bg-blue-600 text-white rounded-md text-sm font-medium transition-colors duration-200 hover:bg-blue-700"
              >
                Refresh
              </button>
      
              {/* Minimize / Maximize */}
              <button 
                onClick={() => {
                  setJobVisible(false);
                  setPolling(false);
                }} 
                className="px-3 py-1 bg-gray-200 text-gray-700 rounded-md text-sm font-medium transition-colors duration-200 hover:bg-gray-300"
              >
                Minimize
              </button>


              {/* Stop Job */}
              {/* {jobStatus?.job_status !== "job_creation_failed" &&
              jobStatus?.job_status !== "job_completed" && (
                <button 
                  onClick={async () => {
                    // await fetch(`/api/cloud_task_jobs/${orgId}/${jobId}`, { method: "DELETE" });
                    setPolling(false);
                    setJobStatus(null);
                    setJobVisible(false);
                    setJobId(null);
                  }}
                  className="px-3 py-1 bg-red-600 text-white rounded-md text-sm font-medium transition-colors duration-200 hover:bg-red-700"
                >
                  Stop Execution
                </button>
              )} */}

              {/* Close button (when job is failed or completed) */}
              {(jobStatus?.job_status === "job_creation_failed" || 
                jobStatus?.job_status === "job_completed") && (
                <button
                  onClick={() => {
                    setPolling(false);
                    setJobStatus(null);
                    setJobVisible(false);
                    setJobId(null);
                  }}
                  className="px-3 py-1 bg-green-600 text-white rounded-md text-sm font-medium transition-colors duration-200 hover:bg-green-700"
                >
                  You may now close this progress window
                </button>
              )}
            </div>
          </div>
      
          {/* Progress Bar with figures */}
          {renderProgressBar()}

          {/* Failed / Pending URLs (only if job completed and lists not empty) */}
          {jobStatus?.job_status === "job_completed" && (
            <div className="space-y-2">
              {/* Failed URLs */}
              {jobStatus?.final_response?.failed_url_list?.length > 0 && (
                <details className="bg-red-50 border border-red-200 rounded p-2">
                  <summary className="cursor-pointer font-medium text-red-700 flex justify-between items-center group">
                    <span className="flex items-center">
                      <span className="mr-2 transition-transform duration-200 group-open:rotate-90">▶</span>
                      Failed URLs ({jobStatus.final_response.failed_url_list.length})
                    </span>
                    <button
                      onClick={() =>
                        navigator.clipboard.writeText(
                          jobStatus.final_response.failed_url_list.join("\n")
                        )
                      }
                      className="ml-2 text-xs px-2 py-0.5 bg-gray-200 rounded hover:bg-gray-300"
                    >
                      Copy All
                    </button>
                  </summary>

                  <ul className="mt-2 space-y-1 text-sm list-disc pl-5">
                    {jobStatus.final_response.failed_url_list.map((url, i) => (
                      <li key={i} className="truncate">{url}</li>
                    ))}
                  </ul>
                </details>
              )}

              {/* Pending URLs */}
              {jobStatus?.final_response?.pending_url_list?.length > 0 && (
                <details className="bg-yellow-50 border border-yellow-200 rounded p-2">
                  <summary className="cursor-pointer font-medium text-yellow-700 flex justify-between items-center">
                    <span className="flex items-center">
                      <span className="mr-2 transition-transform duration-200 group-open:rotate-90">▶</span>
                      Pending URLs ({jobStatus.final_response.pending_url_list.length})
                    </span>
                    <button
                      onClick={() =>
                        navigator.clipboard.writeText(
                          jobStatus.final_response.pending_url_list.join("\n")
                        )
                      }
                      className="ml-2 text-xs px-2 py-0.5 bg-gray-200 rounded hover:bg-gray-300"
                    >
                      Copy All
                    </button>
                  </summary>
                  <ul className="mt-2 space-y-1 text-sm list-disc pl-5">
                    {jobStatus.final_response.pending_url_list.map((url, i) => (
                      <li key={i} className="truncate">{url}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* If Minimized → Small Restore Button */}
      {jobStatus && !jobVisible && (
        <button 
        onClick={() => {
          setJobVisible(true);
          setPolling(true);
        }}
        className="text-blue-700 font-medium hover:text-blue-1000 transition-colors duration-200"
      >
        Track Progress of your Job
      </button>
      )}



      {/* new display logic for onclick kv*/}
      {selectedVault && !showImportModal ? (
        <div className="space-y-4">
          {/* Breadcrumb */}
          <div className="flex items-center text-sm text-gray-500 gap-2">
            <button
              onClick={() => setSelectedVault(null)}
              className="flex items-center text-gray-600 hover:text-blue-600 transition gap-2"
            >
              <ChevronLeft className="h-4 w-4" />
              <span className="font-medium">Knowledge Vault</span>
            </button>
            <span className="text-gray-300">/</span>
            <span className="text-gray-800 font-semibold truncate">{selectedVault.title}</span>
            <div className="ml-auto flex items-center gap-2">
              <span className="px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold uppercase tracking-wide">
                {selectedVault.content_type}
              </span>
            </div>
          </div>

          {/* Vault Details */}
          <div className="bg-white rounded-2xl shadow-lg p-5 md:p-6 border border-gray-200 space-y-6">
            {/* Title & meta row */}
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="min-w-0">
                <h3 className="text-xl md:text-2xl font-semibold text-gray-900 truncate">
                  {selectedVault.title}
                </h3>
                <p className="text-sm text-gray-500 mt-1 break-all">
                  {selectedVault.filename || selectedVault.original_url || ""}
                </p>
              </div>
              <div className="flex items-start md:items-center gap-6 text-sm text-gray-600">
                <div>
                  <span className="font-medium text-gray-800">Created:</span>{" "}
                  {new Date(selectedVault.created_at).toLocaleString()}
                </div>
                <div>
                  <span className="font-medium text-gray-800">Words:</span>{" "}
                  {selectedVault.word_count}
                </div>
              </div>
            </div>

            {/* Collapsible FAQs (styled like Content box) */}
            <div className="bg-white rounded-lg border border-gray-100">
              <button
                onClick={() => setFaqsOpen(!faqsOpen)}
                className="w-full flex items-center justify-between px-4 md:px-5 py-3 text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-md text-white">
                    <FilePlus className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900">FAQs</div>
                    <div className="text-xs text-gray-500">
                      {selectedVault.faqs?.length || 0} generated
                    </div>
                  </div>
                </div>
                <div className="text-gray-500">
                  {faqsOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </div>
              </button>

              {faqsOpen && selectedVault.faqs && selectedVault.faqs.length > 0 && (
                <div className="p-4 md:p-5 border-t border-gray-100 space-y-3">
                  {selectedVault.faqs.map((faq, index) => (
                    <div
                      key={`faq-${index}-${faq.question?.substring(0, 50) || index}`}
                      onClick={() => handleFAQNavigation(faq.question)}
                      className="border border-gray-100 rounded-lg overflow-hidden cursor-pointer hover:bg-gray-50 transition"
                    >
                      <div className="w-full flex items-center justify-between px-4 md:px-5 py-3 text-left">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-800 line-clamp-2">
                            Q: {faq.question}
                          </div>
                          <div className="text-xs text-gray-500 line-clamp-1">{faq.context || ""}</div>
                        </div>
                      </div>

                      <div className="px-4 md:px-5 pb-4 text-gray-700">
                        <div className="text-sm">A: {faq.answer}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {faqsOpen && (!selectedVault.faqs || selectedVault.faqs.length === 0) && (
                <div className="p-4 md:p-5 border-t border-gray-100 text-sm text-gray-500">
                  No FAQs generated for this content yet.
                </div>
              )}
            </div>

            {/* Collapsible Content */}
            {selectedVault.content && (
              <div className="bg-white rounded-lg border border-gray-100">
                <button
                  onClick={() => setContentOpen(!contentOpen)}
                  className="w-full flex items-center justify-between px-4 md:px-5 py-3 text-left"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-md text-white">
                      <FileText className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">Context</div>
                      <div className="text-xs text-gray-500">{`${Math.min(
                        200,
                        selectedVault.content.length
                      )} chars preview`}</div>
                    </div>
                  </div>
                  <div className="text-gray-500">
                    {contentOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </div>
                </button>

                {contentOpen && (
                  <div
                    className="p-4 md:p-5 border-t border-gray-100 text-gray-700 prose max-w-none"
                    style={{ maxHeight: "55vh", overflowY: "auto" }}
                  >
                    <ReactMarkdown>{selectedVault.content}</ReactMarkdown>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ) : content.length === 0 ? (
        // no content UI
        <div className="text-center py-12 md:py-16">
          <div className="relative mx-auto w-20 h-20 md:w-24 md:h-24 mb-6">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl"></div>
            <div className="absolute inset-2 bg-white rounded-xl flex items-center justify-center">
              <CloudUpload className="h-7 w-7 md:h-8 md:w-8 text-blue-500" />
            </div>
          </div>
          <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-2">No content yet</h3>
          <p className="text-gray-500 mb-6 md:mb-8 max-w-md mx-auto">
            Get started by uploading your first piece of content. We support text, PDFs, DOCX, CSV files, and web URLs.
          </p>
          <button
            onClick={() => {
              setSelectedVault(null);
              setShowUploadModal(true);
            }}
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-medium rounded-xl shadow-lg hover:shadow-xl hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 transform hover:-translate-y-0.5"
          >
            <FilePlus className="h-5 w-5 mr-2" />
            Upload Content
          </button>
        </div>
      ) : (
        //normal grid UI
        <div className="grid grid-cols-1 gap-5 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {content.map((item, index) => {
            const Icon = getContentIcon(item.content_type);
            return (
              <div
                key={`${item.content_id}-${index}`}
                onClick={(e) => {
                  if (e.target.closest("button")) return;
                  setSelectedVault(item);
                }}
                className="cursor-pointer group bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/60 hover:shadow-2xl hover:border-blue-300/70 hover:bg-white transition-all duration-300 hover:-translate-y-1.5 hover:scale-[1.01] active:scale-95 active:shadow-md active:border-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-600/40"
              >
                <div className="p-5 md:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg mr-3 group-hover:scale-110 transition-transform duration-200">
                        <Icon className="h-5 w-5 text-white" />
                      </div>
                      <span className="text-xs uppercase tracking-wide text-gray-600 font-semibold bg-gray-100 px-2 py-1 rounded-full">
                        {item.content_type}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDelete(item.content_id)}
                      className="text-gray-400 hover:text-red-500 p-2 rounded-lg hover:bg-red-50 transition-all duration-200"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors duration-200 line-clamp-2">
                    {item.title}
                  </h3>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">FAQs Generated:</span>
                      <span className="font-medium text-gray-900 bg-blue-100 px-2 py-1 rounded-full text-xs">
                        {item.faqs?.length || 0}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Created:</span>
                      <span className="text-gray-700">
                        {new Date(item.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  {item.content && (
                    <div className="text-sm text-gray-600 bg-gradient-to-br from-gray-50 to-gray-100 p-3 md:p-4 rounded-xl border border-gray-200">
                      <div className="max-w-none text-gray-700 leading-relaxed line-clamp-4">
                        {item.content.length > 200
                          ? `${item.content.substring(0, 200)}...`
                          : item.content}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div
          className="fixed inset-0 overflow-hidden z-50"
          style={{
            height: "100%",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
        >
          {/* Semi-transparent backdrop */}
          <div
            className="fixed inset-0 bg-black/30 backdrop-blur-sm"
            onClick={() => setShowUploadModal(false)}
          ></div>

          {/* Modal container - absolute center positioning */}
          <div className="flex items-center justify-center min-h-screen px-4 py-8">
            <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-md mx-auto p-6 max-h-[85vh] overflow-y-auto">
              <form onSubmit={handleUpload} className="space-y-5">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">Title</label>
                    <span className="text-[10px] font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-100">
                      Auto-generated if empty
                    </span>
                  </div>
                  <input
                    type="text"
                    value={uploadForm.title}
                    onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200"
                    placeholder="Enter content title (optional)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Content Type</label>
                  <select
                    value={uploadForm.contentType}
                    onChange={(e) => setUploadForm({ ...uploadForm, contentType: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200"
                  >
                    <option value="text">Text Content</option>
                    <option value="pdf">PDF File</option>
                    <option value="docx">DOCX File</option>
                    <option value="csv">CSV File</option>
                    <option value="url">Website URL</option>
                  </select>
                </div>

                {uploadForm.contentType === "text" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Content</label>
                    <textarea
                      value={uploadForm.content}
                      onChange={(e) => setUploadForm({ ...uploadForm, content: e.target.value })}
                      rows={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200 resize-none"
                      placeholder="Enter your content here..."
                      required
                    />
                  </div>
                )}
                {uploadForm.contentType === "pdf" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">PDF File</label>
                    <div className="relative">
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files[0] })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        required
                      />
                    </div>
                  </div>
                )}
                {uploadForm.contentType === "docx" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Docx</label>
                    <div className="relative">
                      <input
                        type="file"
                        accept=".docx"
                        onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files[0] })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        required
                      />
                    </div>
                  </div>
                )}

                {uploadForm.contentType === "csv" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">CSV File</label>
                    <div className="relative">
                      <input
                        type="file"
                        accept=".csv"
                        onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files[0] })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        required
                      />
                    </div>
                  </div>
                )}

                {uploadForm.contentType === "url" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Website URL</label>
                    <input
                      type="url"
                      value={uploadForm.content}
                      onChange={(e) => setUploadForm({ ...uploadForm, content: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200"
                      placeholder="https://example.com"
                      required
                    />
                  </div>
                )}

                <div className="flex justify-end gap-2 md:gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowUploadModal(false)}
                    className="px-5 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={uploading}
                    className="px-5 py-3 text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl hover:from-blue-600 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center gap-2"
                  >
                    {uploading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        <span>Uploading...</span>
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4" />
                        <span>Upload & Generate FAQs</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Import Website Modal */}
      {showImportModal && (
        <div
          className="fixed inset-0 overflow-hidden z-50"
          style={{ height: "100%", position: "fixed", top: 0, left: 0, right: 0, bottom: 0 }}
        >
          <div
            className="fixed inset-0 bg-black/30 backdrop-blur-sm"
            onClick={() => setShowImportModal(false)}
          ></div>
          <div className="flex items-center justify-center min-h-screen px-4 py-8">
            <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-5xl mx-auto p-6 h-[90vh] overflow-hidden">
              <div className="space-y-4 h-full flex flex-col">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Select URLs for FAQ Generation</h3>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={refreshSitemapDiscovery}
                      disabled={discovering}
                      className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                    >
                      {discovering ? "Refreshing..." : "Refresh"}
                    </button>
                    <span className="text-sm text-gray-500">{selectedUrls.size} URL(s) selected</span>
                  </div>
                </div>

                {discovering ? (
                  <div className="flex items-center justify-center py-10">
                    <div className="w-8 h-8 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
                    <span className="ml-3 text-gray-600">Discovering sitemap URLs...</span>
                  </div>
                ) : sitemapUrls.length === 0 ? (
                  <div className="text-sm text-gray-600">
                    No URLs found from sitemap. Ensure your organization domain has a valid sitemap.xml.
                  </div>
                ) : (
                  <div className="space-y-3 overflow-y-auto pr-1 flex-1">
                    {Object.keys(groupedUrls).map((group) => (
                      <div key={group} className="border border-gray-200 rounded-xl">
                        <div className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-t-xl">
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={groupAllChecked(group)}
                              onChange={() => toggleGroup(group)}
                              className="h-4 w-4"
                            />
                            <button
                              type="button"
                              className="text-left font-medium text-gray-800"
                              onClick={() => toggleGroupExpand(group)}
                            >
                              {group}{" "}
                              <span className="text-gray-500 text-sm">
                                ({groupedUrls[group]?.length || 0} URLs)
                              </span>
                            </button>
                          </div>
                          <button
                            type="button"
                            onClick={() => toggleGroupExpand(group)}
                            className="text-gray-500"
                          >
                            {groupExpanded[group] ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </button>
                        </div>
                        {groupExpanded[group] && (
                          <div className="px-3 py-2 divide-y divide-gray-100">
                            {groupedUrls[group].map((u, urlIndex) => (
                              <label
                                key={`${group}-${urlIndex}-${u}`}
                                className={`flex items-center gap-3 py-2 rounded-md px-1 ${isUrlProcessed(u) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'}`}
                                title={isUrlProcessed(u) ? 'FAQs already generated for this URL' : ''}
                              >
                                <input
                                  type="checkbox"
                                  checked={isChecked(u)}
                                  onChange={() => toggleUrl(u)}
                                  disabled={isUrlProcessed(u)}
                                  className="h-4 w-4"
                                />
                                <span className="text-sm text-gray-700 break-all flex-1">{toDisplayPath(u)}</span>
                                {isUrlProcessed(u) && (
                                  <span className="text-[10px] uppercase tracking-wide bg-gray-100 text-gray-600 px-2 py-1 rounded-full">Processed</span>
                                )}
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex items-center justify-between pt-2">
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleSelectAll}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors duration-200"
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={handleClearAll}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors duration-200"
                    >
                      Clear All
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setShowImportModal(false)}
                      className="px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors duration-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleQueueSelectedUrl}
                      disabled={discovering || queuing || selectedUrls.size === 0}
                      className="px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl hover:from-emerald-600 hover:to-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center gap-2"
                    >
                      {queuing ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          <span>Queuing...</span>
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4" />
                          <span>
                            Generate FAQs ({selectedUrls.size} URL{selectedUrls.size === 1 ? "" : "s"})
                          </span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default KnowledgeVault;
